import { useResource } from '@landsbankinn/l16n';
import {
  Box,
  Col,
  InputContainer,
  InputValidationMessage,
  Label,
  P,
  PatternInput,
} from '@landsbankinn/ui-kit';
import { useRef, useState } from 'react';
import { Loading } from 'src/components/Loading';
import { loginRes } from 'src/components/Login/Login.res';
import { LoginHeaders } from 'src/components/Login/LoginHeaders';
import { setPreviousLoginType } from 'src/components/Login/utils';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  AllowedLoginType,
  type LoginServerState,
  useApplicationClientContext,
  useApplicationServerContext,
} from 'src/state';
import { LoginButtons } from '../LoginButtons';
import { LoginForm } from '../LoginForm';
import { res } from './SmsForm.res';
import { useSmsSubmit } from './useSmsSubmit';
import { useSmsValidation } from './useSmsValidation';

export function SmsForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phonenumber, setPhonenumber] = useState<string | undefined>();
  const { r } = useResource();
  const { isMobile } = useScreenSize();

  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [clientState, clientDispatch] = useApplicationClientContext();
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const { postSmsForm } = useSmsSubmit({
    onPhonenumber: setPhonenumber,
    onSubmitting: (submitting: boolean) => {
      setIsSubmitting(submitting);

      clientDispatch({
        type: 'setIsSubmitting',
        payload: { isSubmitting: submitting },
      });
    },
    xsrf:
      clientState.form?.xsrf || serverState.forms.sms.fields.xsrf.value || '',
  });
  const { onCodeChanged, smsState } = useSmsValidation();

  function getDescriptionText() {
    let formattedPhoneNumber = '';
    if (phonenumber) {
      if (phonenumber.length === 7) {
        formattedPhoneNumber = `${phonenumber.substring(0, 3)} ${phonenumber.substring(3)}`;
      } else if (phonenumber.length === 11) {
        formattedPhoneNumber = `${phonenumber.substring(0, 4)} ${phonenumber.substring(4, 7)} ${phonenumber.substring(7)}`;
      } else {
        formattedPhoneNumber = phonenumber;
      }
    }
    return `${r(res.info)} ${formattedPhoneNumber}`;
  }

  const authenticationNumberRef = useRef<HTMLInputElement>(null);
  return (
    <LoginForm
      form={serverState.forms.sms}
      onSubmit={(e) => {
        if (smsState.code.validationState !== 'valid') {
          setShowValidation(true);
          return;
        }
        e.preventDefault();
        setPreviousLoginType(AllowedLoginType.Password);
        postSmsForm(e.target as HTMLFormElement);
      }}
      fieldValueProvider={(name) => {
        if (name === 'xsrf' && clientState.form?.xsrf) {
          return clientState.form?.xsrf;
        }
        return serverState.forms.sms.fields[name].value;
      }}
    >
      {isSubmitting && (
        <Col
          minHeight={isMobile ? undefined : '580'}
          mainAxisAlignment={isMobile ? undefined : 'center'}
        >
          <Loading
            title={r(loginRes.loadingTitle)}
            description={r(loginRes.loadingDescription)}
          />
        </Col>
      )}
      {!isSubmitting && (
        <>
          <LoginHeaders />
          <Box marginVertical="36">
            <Col height="100%">
              <Box marginBottom="20">
                <P fontVariant="label">{getDescriptionText()}.</P>
              </Box>
              <InputContainer
                direction="col"
                width="100%"
                validationState={smsState.code.validationState}
                variant="text input col with validation"
              >
                <Label>{r(res.authenticationNumber)}</Label>
                <PatternInput
                  id="authenticationNumber"
                  inputMode="numeric"
                  variant="hero"
                  ref={authenticationNumberRef}
                  instrumentAs="enter_authentication_number"
                  name={serverState.forms.sms.fields.code.name}
                  format="####"
                  autoFocus={false}
                  autoComplete="off"
                  value={smsState.code.value}
                  onValueChange={(v) => {
                    onCodeChanged(v.value);
                  }}
                />

                {showValidation && (
                  <InputValidationMessage
                    invalidMessage={r(
                      res.validationAuthenticationNumberInvalid,
                    )}
                    unsetMessage={r(res.validationAuthenticationNumberRequired)}
                  />
                )}
              </InputContainer>
            </Col>
          </Box>
          <LoginButtons
            showRejectButton={serverState.isConsentFlow}
            isValid={smsState.code.validationState === 'valid'}
            loginType={AllowedLoginType.Password}
          />
        </>
      )}
      {(serverState.environment === 'Local' ||
        serverState.environment === 'Development') && (
        <P fontVariant="label" paddingTop="32" fontWeight="bold">
          {
            'You are running on a local- or development environment. Enter any 4 digit number'
          }
        </P>
      )}
    </LoginForm>
  );
}
